import type { FC } from 'react';
import React, { useRef, useMemo, useEffect } from 'react';
import arrayMutators from 'final-form-arrays';
import type { FormApi } from 'final-form';
import { useActions } from '@alfa-bank/site-form-utils/hooks/use-actions';
import { onComplete } from '@alfa-bank/site-form-utils/mutators/on-complete';
import { useBusinessLogging } from '@alfa-bank/site-form-utils/decorators/business-logging';
import {
    PUBLISHER_EVENT,
    useSendAnalytics,
} from '@alfa-bank/site-form-utils/decorators/send-analytics';
import { scrollToFirst } from '@alfa-bank/site-form-utils/decorators/scroll-to-first';
import { useHashFocus } from '@alfa-bank/site-form-utils/decorators/hash-focus';
import { getFieldsOrder } from '@alfa-bank/site-form-utils/misc/get-fields-order';

import { PaginationProvider } from './pagination/pagination-provider';
import { usePagination } from './pagination/use-pagination';
import type { TForm, TFormValues, TCallbackSubmitForm } from './models';
import { useForkRef } from './hooks/use-fork-ref';

export const Form: FC<React.PropsWithChildren<TForm>> = ({
    DataLayer,
    FinalForm,
    actionPath,
    analytics,
    bannerId,
    businessLoggingSource: source,
    children,
    dataTestId = 'form',
    focusEnabled = true,
    formAction: formActionObject,
    formHash,
    id = 'form',
    initialValues,
    inputsOrder,
    onSubmit,
    platformId,
    metricEvent = PUBLISHER_EVENT,
    noValidate = false,
    product,
    encType,
    styles,
    validateOnBlur = true,
    className,
    mutators,
    preventSubmit,
    forwardedRef,
}) => {
    const actionsInit = {
        bannerId,
        platformId,
        product,
        ...formActionObject,
    };
    const pagination = usePagination();
    const formRef = useRef<HTMLFormElement>(null);
    const [action, onUpdateFormAction] = useActions(actionsInit, actionPath);
    const forkedRef = useForkRef(formRef, forwardedRef);

    // Обновляет formAction при изменении объекта actions из пропсов
    useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/no-shadow
        const { bannerId, platformId, ...rest } = actionsInit;
        onUpdateFormAction([{ ...rest }]);
    }, [product, formActionObject]);

    const submitForm = (
        _: TFormValues,
        form: FormApi<TFormValues>,
        callback?: TCallbackSubmitForm
    ) => {
        if (!formRef.current) return;
        if (!pagination.isLastPage) {
            pagination.nextPage();

            return;
        }
        if (onSubmit) {
            onSubmit(formRef.current, form, callback || (() => {}));

            return;
        }
        if (!preventSubmit) {
            formRef.current.submit();
        }
    };

    const { fieldsOrder } = useMemo(
        () => getFieldsOrder(children, inputsOrder),
        [children, inputsOrder]
    );

    return (
        <div className={className}>
            <FinalForm<TFormValues>
                decorators={[
                    scrollToFirst,
                    useSendAnalytics(analytics),
                    useHashFocus(formHash, focusEnabled),
                    useBusinessLogging({ actions: actionsInit, product, source }),
                ]}
                initialValues={initialValues}
                mutators={{
                    ...arrayMutators,
                    onComplete: onComplete(formRef, fieldsOrder),
                    onUpdateFormAction,
                    ...mutators,
                }}
                onSubmit={submitForm}
                render={({ handleSubmit }) => (
                    <form
                        ref={forkedRef}
                        action={action}
                        className={styles.full}
                        data-test-id={dataTestId}
                        encType={encType}
                        id={id}
                        method="post"
                        noValidate={noValidate}
                        onSubmit={handleSubmit}
                    >
                        <PaginationProvider pagination={pagination}>{children}</PaginationProvider>
                        {analytics && <DataLayer publisherEvent={metricEvent} />}
                    </form>
                )}
                validateOnBlur={validateOnBlur}
            />
        </div>
    );
};
